import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import { Position } from '@shared/schema';
import { Database, DatabaseMetadata } from '@shared/types';
import { databaseService } from '@/lib/database-service';
import { voiceAPI } from '@/lib/voice-api';
import { AudioRecorder, type RecordingState } from '@/lib/audio-recorder';
import { 
  Upload, 
  FileText, 
  ShoppingCart, 
  Download, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Search,
  Plus,
  Minus,
  User,
  Building,
  Calendar,
  Euro,
  Package,
  FolderOpen,
  Loader2,
  AlertCircle,
  Database as DatabaseIcon,
  RefreshCw,
  Mic,
  Square,
  Sparkles,
  FileSpreadsheet,
} from 'lucide-react';
import { parseExcelFile } from '@/lib/simple-parser';
import { exportQuoteDocx } from '@/lib/document-export';
import { Logo } from '@/components/ui/logo';
import MobileProgress from '@/components/mobile-progress';
import FileManager from '@/components/file-manager';
import { sendWebhookData } from '@/lib/webhook-service';
import MobileDebug from '@/components/mobile-debug';
import './mobile-animations.css';
import kranBackground from '../assets/Kran.png';

type MobilePhase = 'input' | 'processing' | 'output';

interface MobileAppState {
  phase: MobilePhase;
  uploadedData: Position[] | null;
  selectedDatabase: Database | null;
  customerName: string;
  projectName: string;
  searchTerm: string;
  selectedItems: Map<number, number>; // item id -> quantity
  isLoading: boolean;
  error: string | null;
  databases: DatabaseMetadata[];
  showDatabaseList: boolean;
  
  // Enhanced project information (desktop parity)
  projectInfo: {
    kunde: string;
    anschrift: string;
    datum: string;
    bauvorhaben: string;
    gewerk: string;
    auftrag: string;
    eingereichte_unterlagen: string;
    lz: string;
    angebot_nr: string;
  };
  
  // Export configuration (desktop parity)
  exportConfig: {
    method: string; // DocxGenerationMethod
    customTemplate: File | null;
    hasSavedTemplate: boolean;
    checkingTemplate: boolean;
    showExportMethodSelection: boolean;
    pdfSubtotalSupported: boolean;
  };
  
  // Voice recording state
  isRecording: boolean;
  recordingState: RecordingState;
  isProcessingVoice: boolean;
  voiceTranscription: string;
  showVoiceRecorder: boolean;
  
  // Enhanced UI state
  showProjectInfoEditor: boolean;
  showTemplateUpload: boolean;
  exportSuccess: boolean;
}

export default function MobileApp() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [state, setState] = useState<MobileAppState>({
    phase: 'input',
    uploadedData: null,
    selectedDatabase: null,
    customerName: '',
    projectName: '',
    searchTerm: '',
    selectedItems: new Map(),
    isLoading: false,
    error: null,
    databases: [],
    showDatabaseList: false,
    // Enhanced project information (desktop parity)
    projectInfo: {
      kunde: '',
      anschrift: '',
      datum: '',
      bauvorhaben: '',
      gewerk: '',
      auftrag: '',
      eingereichte_unterlagen: '',
      lz: '',
      angebot_nr: '',
    },
    // Export configuration (desktop parity)
    exportConfig: {
      method: '',
      customTemplate: null,
      hasSavedTemplate: false,
      checkingTemplate: false,
      showExportMethodSelection: false,
      pdfSubtotalSupported: false,
    },
    // Voice recording state
    isRecording: false,
    recordingState: { isRecording: false, isPaused: false, duration: 0 } as RecordingState,
    isProcessingVoice: false,
    voiceTranscription: '',
    showVoiceRecorder: false,
    // Enhanced UI state
    showProjectInfoEditor: false,
    showTemplateUpload: false,
    exportSuccess: false
  });

  const quoteItems = useOfferStore((state) => state.quoteItems);
  const setQuoteItems = useOfferStore((state) => state.setQuoteItems);
  const setQuoteHeader = useOfferStore((state) => state.setQuoteHeader);
  const setPositions = useOfferStore((state) => state.setPositions);

  // Audio recorder instance
  const [audioRecorder, setAudioRecorder] = useState<AudioRecorder | null>(null);

  // Load databases on mount
  useEffect(() => {
    loadDatabases();
    
    // Initialize audio recorder
    if (AudioRecorder.isSupported()) {
      const recorder = new AudioRecorder(
        {
          maxDuration: 25 * 60, // 25 minutes
          sampleRate: 44100,
          channels: 1
        },
        (recordingState) => {
          setState(prev => ({ 
            ...prev, 
            recordingState,
            isRecording: recordingState.isRecording 
          }));
        }
      );
      setAudioRecorder(recorder);
    }
    
    return () => {
      // Cleanup audio recorder
      if (audioRecorder) {
        audioRecorder.cancelRecording();
      }
    };
  }, []);

  // Load available databases
  const loadDatabases = useCallback(async () => {
    try {
      const databases = await databaseService.listDatabases({ status: 'active' });
      setState(prev => ({ ...prev, databases }));
    } catch (error) {
      console.error('Failed to load databases:', error);
    }
  }, []);

  // Handle database selection
  const handleDatabaseSelect = useCallback(async (databaseMetadata: DatabaseMetadata) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const fullDatabase = await databaseService.loadDatabase(databaseMetadata.id);
      
      // Convert database entries to positions format
      // For quote databases, keep existing quantities; for others, start with 0
      const positions = fullDatabase.entries.map((entry, index) => ({
        id: index + 1,
        originalId: entry.artikelId || (index + 1).toString(),
        gewerk: entry.gewerk || '',
        name: entry.name || '',
        beschreibung: entry.beschreibung || '',
        einheit: entry.einheit || 'Stk',
        preis: parseFloat(entry.preis?.toString() || '0'),
        umsatzsteuer: parseFloat(entry.mwst?.toString() || '19'),
        // Keep existing quantities for quote databases, otherwise start with 0
        menge: fullDatabase.type === 'quote' ? (entry.menge || 0) : 0,
      }));

      // Set positions in store
      setPositions(positions);
      
      // For quote databases, also create quoteItems from positions with quantities > 0
      if (fullDatabase.type === 'quote') {
        const quoteItemsForStore = positions
          .filter(pos => pos.menge > 0)
          .map(pos => ({
            id: pos.id,
            originalId: pos.originalId || String(pos.id),
            name: pos.name || '',
            beschreibung: pos.beschreibung,
            gewerk: pos.gewerk || '',
            einheit: pos.einheit,
            preis: pos.preis,
            umsatzsteuer: pos.umsatzsteuer,
            menge: pos.menge
          }));
        
        // Set the quote items in the store
        setQuoteItems(quoteItemsForStore);
        
        console.log(`Mobile: Loaded quote database with ${positions.length} positions and ${quoteItemsForStore.length} quote items`);
      } else {
        // For non-quote databases, clear quote items
        setQuoteItems([]);
        console.log(`Mobile: Loaded database with ${positions.length} positions (all with menge 0 for editing)`);
      }
      
      setState(prev => ({ 
        ...prev, 
        selectedDatabase: fullDatabase,
        uploadedData: positions,
        isLoading: false 
      }));
      
      const mengenInfo = fullDatabase.type === 'quote' ? 'mit vorhandenen Mengen' : 'alle mit Menge 0 zum Bearbeiten';
      toast({
        title: "Datenbank geladen",
        description: `${fullDatabase.name} geladen (${mengenInfo})`,
        variant: "success",
      });
      
    } catch (error) {
      console.error('Failed to load database:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Fehler beim Laden der Datenbank',
        isLoading: false 
      }));
      
      toast({
        title: "Fehler",
        description: "Datenbank konnte nicht geladen werden.",
        variant: "destructive",
      });
    }
  }, [toast, setQuoteItems, setPositions]);

  // Clear error when state changes
  useEffect(() => {
    if (state.error) {
      setState(prev => ({ ...prev, error: null }));
    }
  }, [state.phase, state.uploadedData]);

  // Phase transition handlers
  const goToProcessing = useCallback(() => {
    if (!state.uploadedData?.length) {
      toast({
        title: "Keine Daten",
        description: "Bitte wählen Sie eine Datenbank oder laden Sie eine Excel-Datei hoch.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setState(prev => ({ ...prev, phase: 'processing', error: null }));
    } catch (error) {
      console.error('Error transitioning to processing phase:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Fehler beim Wechseln zur Bearbeitungsphase' 
      }));
    }
  }, [state.uploadedData, toast]);

  const goToOutput = useCallback(() => {
    if (state.selectedItems.size === 0) {
      toast({
        title: "Keine Auswahl",
        description: "Bitte wählen Sie mindestens eine Position aus.",
        variant: "destructive"
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Convert selected items to quote format
      const selectedPositions = Array.from(state.selectedItems.entries())
        .map(([itemId, quantity]) => {
          const position = state.uploadedData?.find(p => p.id === itemId);
          if (!position || quantity <= 0) return null;
          
          return {
            id: position.id,
            originalId: String(position.id),
            name: position.name || position.beschreibung,
            beschreibung: position.beschreibung,
            einheit: position.einheit,
            preis: Number(position.preis),
            menge: quantity,
            gewerk: position.gewerk || 'Allgemein',
            umsatzsteuer: position.umsatzsteuer || 19
          };
        })
        .filter(Boolean);

      setQuoteItems(selectedPositions.filter((p): p is NonNullable<typeof p> => p !== null));
      setQuoteHeader({
        kunde: state.customerName || 'Mobile Kunde',
        angebot_nr: `A-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`,
        datum: new Date().toISOString().split('T')[0],
        anschrift: '',
        bauvorhaben: state.projectName || '',
        gewerk: '',
        auftrag: '',
        eingereichte_unterlagen: '',
        lz: ''
      });

      setState(prev => ({ ...prev, phase: 'output', isLoading: false }));
    } catch (error) {
      console.error('Error transitioning to output phase:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Fehler beim Erstellen des Angebots' 
      }));
      toast({
        title: "Fehler",
        description: "Das Angebot konnte nicht erstellt werden.",
        variant: "destructive"
      });
    }
  }, [state.selectedItems, state.uploadedData, state.customerName, state.projectName, setQuoteItems, setQuoteHeader, toast]);

  const goBack = useCallback(() => {
    try {
      setState(prev => ({
        ...prev,
        phase: prev.phase === 'output' ? 'processing' : 'input',
        error: null
      }));
    } catch (error) {
      console.error('Error going back:', error);
    }
  }, []);

  // File upload handler with better error handling
  const handleFileUpload = useCallback(async (file: File) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Validate file size (limit to 10MB for mobile)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Datei ist zu groß. Maximum: 10MB');
      }

      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv|ods)$/i)) {
        throw new Error('Ungültiger Dateityp. Nur Excel-Dateien (.xlsx, .xls) und CSV sind erlaubt.');
      }

      const parsedData = await parseExcelFile(file);
      
      if (!parsedData || !parsedData.positions || parsedData.positions.length === 0) {
        throw new Error('Keine gültigen Daten in der Datei gefunden.');
      }

      setState(prev => ({ 
        ...prev, 
        uploadedData: parsedData.positions, 
        isLoading: false,
        error: null 
      }));
      
      // Send webhook data (non-blocking)
      try {
        const webhookSuccess = await sendWebhookData(file.name, parsedData.positions);
        if (webhookSuccess) {
          console.log('Mobile: Webhook data sent successfully');
        }
      } catch (webhookError) {
        console.error('Mobile: Webhook error (non-blocking):', webhookError);
        // Don't show error to user for webhook failures
      }
      
      toast({
        title: "Datei hochgeladen",
        description: `${parsedData.positions.length} Positionen erfolgreich geladen.`
      });
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = error instanceof Error ? error.message : "Datei konnte nicht gelesen werden.";
      
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: errorMessage 
      }));
      
      toast({
        title: "Upload-Fehler",
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, [toast]);

  // Item selection handlers
  const updateQuantity = useCallback((itemId: number, delta: number) => {
    try {
      setState(prev => {
        const newSelected = new Map(prev.selectedItems);
        const currentQty = newSelected.get(itemId) || 0;
        const newQty = Math.max(0, currentQty + delta);
        
        if (newQty === 0) {
          newSelected.delete(itemId);
        } else {
          newSelected.set(itemId, newQty);
        }
        
        return { ...prev, selectedItems: newSelected };
      });
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }, []);

  // Filtered items for processing phase
  const filteredItems = state.uploadedData?.filter(item =>
    state.searchTerm === '' || 
    item.beschreibung.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
    item.gewerk?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
    item.name?.toLowerCase().includes(state.searchTerm.toLowerCase())
  ) || [];

  // Calculate totals
  const totals = Array.from(state.selectedItems.entries()).reduce((acc, [itemId, quantity]) => {
    const item = state.uploadedData?.find(p => p.id === itemId);
    if (item) {
      const lineTotal = item.preis * quantity;
      const taxAmount = lineTotal * ((item.umsatzsteuer || 19) / 100);
      acc.net += lineTotal;
      acc.tax += taxAmount;
    }
    return acc;
  }, { net: 0, tax: 0 });

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  // Simplified DOCX+CSV export function
  const handleExport = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      console.log('🔄 Mobile DOCX+CSV export starting...', {
        userAgent: navigator.userAgent,
        isMobile: navigator.userAgent.match(/iPhone|iPad|iPod|Android/i) ? true : false,
        quoteItemsCount: quoteItems.length
      });
      
      // Use the template-based DOCX generator with CSV
      const { saveDocx } = await import('@/lib/docx-generator-template');
      
      // Build header data
      const headerData = {
        kunde: state.customerName || 'Mobile Kunde',
        angebot_nr: `A-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`,
        datum: new Date().toISOString().split('T')[0],
        anschrift: '',
        bauvorhaben: state.projectName || '',
        gewerk: '',
        auftrag: '',
        eingereichte_unterlagen: '',
        lz: ''
      };
      
      // Generate filename
      const customerName = headerData.kunde?.trim().replace(/[^a-zA-Z0-9\s]/g, '') || 'Mobile_Kunde';
      const offerNumber = headerData.angebot_nr?.trim() || `A-${new Date().getFullYear()}-Mobile`;
      const filename = `${offerNumber} - ${customerName}.docx`;
      
      console.log('📄 Generating DOCX+CSV with template...', { filename, itemCount: quoteItems.length });
      
      // Generate DOCX with automatic CSV
      await saveDocx(headerData, quoteItems, filename);
      
      setState(prev => ({ ...prev, isLoading: false }));
      
      toast({
        title: "Angebot erfolgreich erstellt",
        description: "DOCX und CSV wurden generiert und heruntergeladen.",
        variant: "success"
      });
      
      // Mobile download hint
      if (navigator.userAgent.match(/iPhone|iPad|iPod|Android/i)) {
        setTimeout(() => {
          toast({
            title: "Mobile Download-Hinweis",
            description: "Prüfen Sie Ihren Download-Ordner. Bei Problemen verwenden Sie einen Desktop-Browser.",
            variant: "default"
          });
        }, 2000);
      }
      
      console.log("✅ Mobile DOCX+CSV export completed successfully");
      
    } catch (error) {
      console.error('❌ Mobile export error:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Export fehlgeschlagen' 
      }));
      
      const isMobile = navigator.userAgent.match(/iPhone|iPad|iPod|Android/i);
      const errorMessage = isMobile 
        ? "Das Dokument konnte nicht erstellt werden. Versuchen Sie es mit einem Desktop-Browser."
        : "Das Dokument konnte nicht erstellt werden. Versuchen Sie es erneut.";
      
      toast({
        title: "Export-Fehler",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  // Save quote without export
  const handleSaveQuote = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      console.log('💾 Saving quote without export...', {
        customerName: state.customerName,
        projectName: state.projectName,
        itemCount: quoteItems.length
      });
      
      // Build header data
      const headerData = {
        kunde: state.customerName || 'Mobile Kunde',
        angebot_nr: `A-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`,
        datum: new Date().toISOString().split('T')[0],
        anschrift: '',
        bauvorhaben: state.projectName || '',
        gewerk: '',
        auftrag: '',
        eingereichte_unterlagen: '',
        lz: ''
      };
      
      // Save quote data to server
      const response = await fetch('/api/quote-data/save-and-get', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          header: headerData,
          items: quoteItems,
          gewerkOrder: [],
          metadata: {
            customerName: headerData.kunde,
            offerNumber: headerData.angebot_nr,
            itemCount: quoteItems.length,
            totalValue: quoteItems.reduce((sum, item) => sum + (item.preis * item.menge), 0),
            mobileGenerated: true,
            savedWithoutExport: true,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to save quote: ${response.status}`);
      }

      const savedData = await response.json();
      
      setState(prev => ({ ...prev, isLoading: false }));
      
      toast({
        title: "Angebot gespeichert",
        description: `Angebot "${headerData.angebot_nr}" wurde erfolgreich gespeichert. Sie können es später exportieren.`,
        variant: "success"
      });
      
      console.log("✅ Quote saved successfully:", savedData.metadata);
      
    } catch (error) {
      console.error('❌ Save quote error:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Speichern fehlgeschlagen' 
      }));
      
      toast({
        title: "Speicher-Fehler",
        description: "Das Angebot konnte nicht gespeichert werden. Versuchen Sie es erneut.",
        variant: "destructive"
      });
    }
  };

  // Error display component
  const ErrorDisplay = ({ error }: { error: string }) => (
    <Card className="border-red-200 bg-red-50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      </CardContent>
    </Card>
  );

  // Loading overlay component with enhanced mobile feedback
  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="bg-white p-6 max-w-sm w-full">
        <CardContent className="text-center space-y-4">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {state.phase === 'output' ? 'Export wird erstellt...' : 'Verarbeitung...'}
            </h3>
            <p className="text-sm text-gray-600">
              {state.phase === 'output' 
                ? 'Ihr Angebot wird generiert und zum Download bereitgestellt.'
                : 'Bitte warten Sie einen Moment.'
              }
            </p>
          </div>
          {state.phase === 'output' && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-blue-800">
                <Download className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {state.selectedItems.size} Position{state.selectedItems.size !== 1 ? 'en' : ''} werden verarbeitet
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Voice recording handlers
  const handleStartVoiceRecording = useCallback(async () => {
    if (!audioRecorder) {
      toast({
        title: "Aufnahme nicht unterstützt",
        description: "Ihr Browser unterstützt keine Audioaufnahme.",
        variant: "destructive"
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, error: null }));
      await audioRecorder.startRecording();
    } catch (error) {
      console.error('Failed to start recording:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Mikrofon-Zugriff fehlgeschlagen' 
      }));
      
      toast({
        title: "Aufnahme-Fehler",
        description: "Mikrofon-Zugriff fehlgeschlagen. Bitte Berechtigung erteilen.",
        variant: "destructive"
      });
    }
  }, [audioRecorder, toast]);

  const handleStopVoiceRecording = useCallback(async () => {
    if (!audioRecorder) return;

    try {
      setState(prev => ({ ...prev, isProcessingVoice: true, error: null }));
      
      const audioBlob = await audioRecorder.stopRecording();
      const audioFile = await AudioRecorder.convertForWhisper(audioBlob);
      
      // Process with voice API
      const result = await voiceAPI.voiceToDatabase(
        audioFile,
        `Mobile Datenbank ${new Date().toLocaleDateString('de-DE')}`,
        'German construction price list'
      );

      if (!result.success) {
        throw new Error(result.error || 'Voice processing failed');
      }

      // Set transcription
      setState(prev => ({ 
        ...prev, 
        voiceTranscription: result.transcription || '',
        isProcessingVoice: false 
      }));

      // Convert voice entries to positions
      if (result.entries && result.entries.length > 0) {
        const positions = result.entries.map((entry, index) => ({
          id: index + 1,
          originalId: entry.artikelId || (index + 1).toString(),
          gewerk: entry.gewerk || '',
          name: entry.name || '',
          beschreibung: entry.beschreibung || '',
          einheit: entry.einheit || 'Stk',
          preis: parseFloat(entry.preis?.toString() || '0'),
          umsatzsteuer: parseFloat(entry.mwst?.toString() || '19'),
          menge: 0, // Start with 0 quantity
        }));

        setState(prev => ({ 
          ...prev, 
          uploadedData: positions,
          showVoiceRecorder: false
        }));

        toast({
          title: "Spracheingabe verarbeitet",
          description: `${result.entries.length} Positionen aus Spracheingabe erstellt.`,
          variant: "success"
        });
      } else {
        toast({
          title: "Keine Einträge gefunden",
          description: "Die KI konnte keine Positionen aus der Spracheingabe erstellen.",
          variant: "destructive"
        });
      }

    } catch (error) {
      console.error('Voice processing error:', error);
      setState(prev => ({ 
        ...prev, 
        isProcessingVoice: false,
        error: 'Fehler bei der Sprachverarbeitung' 
      }));
      
      toast({
        title: "Verarbeitungsfehler",
        description: "Die Spracheingabe konnte nicht verarbeitet werden.",
        variant: "destructive"
      });
    }
  }, [audioRecorder, toast]);

  const handleCancelVoiceRecording = useCallback(() => {
    if (audioRecorder) {
      audioRecorder.cancelRecording();
    }
    setState(prev => ({ 
      ...prev, 
      showVoiceRecorder: false,
      voiceTranscription: '',
      isProcessingVoice: false
    }));
  }, [audioRecorder]);

  // Phase-specific renders
  const renderInputPhase = () => (
    <div 
      className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 mobile-input-phase relative"
      style={{
        backgroundImage: `url(${kranBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center bottom',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Loading overlay */}
      {state.isLoading && <LoadingOverlay />}
      
      {/* Background overlay for mobile */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/85 to-indigo-100/85"></div>
      
      {/* Content layer */}
      <div className="relative z-10">
      {/* Progress Indicator */}
      <MobileProgress currentPhase="input" className="sticky top-0 z-20 mobile-progress" />
      
      <div className="space-y-6 p-4">
        
        {/* Error Display */}
        {state.error && <ErrorDisplay error={state.error} />}
        {/* Header */}
        <div className="text-center pt-8 pb-6">
          <div className="flex justify-center mb-4">
            <Logo height={48} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Fortuna Softec</h1>
          <p className="text-gray-600">Angebotstool Mobile</p>
        </div>

        {/* Database Selection */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <DatabaseIcon className="h-5 w-5 text-blue-600" />
              Datenbank wählen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {state.selectedDatabase ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-800 mb-2">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Datenbank ausgewählt</span>
                </div>
                <p className="text-sm text-green-700 font-medium">{state.selectedDatabase.name}</p>
                <p className="text-xs text-green-600 mt-1">
                  {state.uploadedData?.length} Positionen verfügbar
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setState(prev => ({ 
                    ...prev, 
                    selectedDatabase: null, 
                    uploadedData: null,
                    showDatabaseList: true 
                  }))}
                  className="mt-2 text-xs"
                >
                  Andere Datenbank wählen
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Database Selection Buttons */}
                <div className="grid grid-cols-1 gap-3">
                  <Button
                    onClick={() => setState(prev => ({ ...prev, showDatabaseList: !prev.showDatabaseList }))}
                    className="w-full h-12 text-lg font-medium bg-blue-600 hover:bg-blue-700 mobile-button-primary mobile-touch-feedback"
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Lade...
                      </>
                    ) : (
                      <>
                        <DatabaseIcon className="mr-2 h-5 w-5" />
                        Bestehende Datenbank
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={() => setLocation('/excel-import')}
                    className="w-full h-12 text-lg font-medium bg-green-600 hover:bg-green-700 mobile-button-primary mobile-touch-feedback"
                    disabled={state.isLoading}
                  >
                    <FileSpreadsheet className="mr-2 h-5 w-5" />
                    Excel-Datenbank laden
                  </Button>

                  <Button
                    onClick={() => setLocation('/voice-database-builder')}
                    className="w-full h-12 text-lg font-medium bg-purple-600 hover:bg-purple-700 mobile-button-primary mobile-touch-feedback"
                    disabled={state.isLoading}
                  >
                    <Mic className="mr-2 h-5 w-5" />
                    Mit Sprache erstellen
                  </Button>
                </div>
                
                {state.showDatabaseList && (
                  <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Verfügbare Datenbanken:</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadDatabases}
                        disabled={state.isLoading}
                      >
                        <RefreshCw className={`h-3 w-3 ${state.isLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                    
                    {state.databases.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Keine Datenbanken verfügbar
                      </p>
                    ) : (
                      state.databases.map((db) => {
                        const colorInfo = databaseService.getTypeColorInfo(db.type, db.hasQuantities);
                        const displayName = databaseService.getDisplayName(db);
                        
                        return (
                          <Card 
                            key={db.id} 
                            className={`cursor-pointer hover:bg-blue-50 transition-colors mobile-touch-feedback ${colorInfo.className}`}
                            onClick={() => handleDatabaseSelect(db)}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm">{colorInfo.icon}</span>
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {db.name}
                                    </p>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {db.stats?.entryCount || 0} Positionen • {displayName}
                                  </p>
                                </div>
                                <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Info */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-green-600" />
              Projektinfo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="customer">Kunde</Label>
              <Input
                id="customer"
                value={state.customerName}
                onChange={(e) => setState(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="Firmenname oder Name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="project">Projekt (optional)</Label>
              <Input
                id="project"
                value={state.projectName}
                onChange={(e) => setState(prev => ({ ...prev, projectName: e.target.value }))}
                placeholder="Projektname oder Beschreibung"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Continue Button */}
        <div className="pt-4">
          <Button 
            onClick={goToProcessing} 
            className="w-full h-12 text-lg font-medium bg-blue-600 hover:bg-blue-700 mobile-button-primary mobile-touch-feedback"
            disabled={!state.uploadedData?.length || state.isLoading}
          >
            {state.isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Verarbeitung...
              </>
            ) : (
              <>
                {state.selectedDatabase ? 'Weiter mit Datenbank' : 'Weiter zur Auswahl'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </div>
        </div>
      </div>
    </div>
  );

  const renderProcessingPhase = () => (
    <div className="min-h-screen bg-gray-50 mobile-processing-phase">
      {/* Loading overlay */}
      {state.isLoading && <LoadingOverlay />}
      
      {/* Progress Indicator */}
      <MobileProgress currentPhase="processing" className="sticky top-0 z-20 mobile-progress" />
      
      {/* Fixed Header */}
      <div className="sticky top-[88px] bg-white shadow-sm border-b z-10">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" onClick={goBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Zurück
            </Button>
            <div className="text-center">
              <h1 className="font-semibold text-gray-900">Positionen wählen</h1>
              {state.selectedDatabase && (
                <p className="text-xs text-blue-600 mt-1">
                  Aus: {state.selectedDatabase.name}
                </p>
              )}
            </div>
            <div className="w-16" /> {/* Spacer */}
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={state.searchTerm}
              onChange={(e) => setState(prev => ({ ...prev, searchTerm: e.target.value }))}
              placeholder="Suchen..."
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {state.error && (
        <div className="p-4">
          <ErrorDisplay error={state.error} />
        </div>
      )}

      {/* Items List */}
      <div className="p-4 space-y-3 pb-20">
        {filteredItems.length === 0 && !state.isLoading ? (
          <Card className="p-6 text-center">
            <CardContent>
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">
                {state.searchTerm ? 'Keine Positionen gefunden' : 'Keine Daten verfügbar'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredItems.map((item) => {
          const quantity = state.selectedItems.get(item.id) || 0;
          const isSelected = quantity > 0;
          
                     return (
             <Card key={item.id} className={`mobile-item-card mobile-card-enter ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50 mobile-card-selected' : 'bg-white'}`}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {/* Item Info */}
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm leading-tight">
                      {item.name || item.beschreibung}
                    </h3>
                    {item.gewerk && (
                      <p className="text-xs text-blue-600 mt-1">{item.gewerk}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-1">
                      {formatCurrency(item.preis)} / {item.einheit}
                    </p>
                  </div>
                  
                  {/* Quantity Controls */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                                             <Button
                         variant="outline"
                         size="sm"
                         onClick={() => updateQuantity(item.id, -1)}
                         disabled={quantity === 0}
                         className="h-8 w-8 p-0 mobile-quantity-btn mobile-touch-feedback"
                       >
                         <Minus className="h-3 w-3" />
                       </Button>
                       
                       <span className="w-8 text-center font-medium">
                         {quantity}
                       </span>
                       
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => updateQuantity(item.id, 1)}
                         className="h-8 w-8 p-0 mobile-quantity-btn mobile-touch-feedback"
                       >
                         <Plus className="h-3 w-3" />
                       </Button>
                    </div>
                    
                    {isSelected && (
                      <div className="text-right">
                        <p className="text-sm font-medium text-green-700">
                          {formatCurrency(item.preis * quantity)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
        )}
      </div>

      {/* Fixed Bottom Bar */}
      {state.selectedItems.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-600">
                {state.selectedItems.size} Position{state.selectedItems.size !== 1 ? 'en' : ''}
              </p>
              <p className="font-semibold text-lg">
                {formatCurrency(totals.net + totals.tax)}
              </p>
            </div>
            <Button 
              onClick={goToOutput} 
              className="bg-green-600 hover:bg-green-700"
              disabled={state.isLoading}
            >
              {state.isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Erstelle...
                </>
              ) : (
                <>
                  Angebot erstellen
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderOutputPhase = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 mobile-output-phase">
      {/* Loading overlay */}
      {state.isLoading && <LoadingOverlay />}
      
      {/* Progress Indicator */}
      <MobileProgress currentPhase="output" className="sticky top-0 z-20 mobile-progress" />
      
      <div className="p-4">
        
        {/* Error Display */}
        {state.error && <ErrorDisplay error={state.error} />}
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pt-4">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Bearbeiten
          </Button>
          <h1 className="font-semibold text-gray-900">Angebot fertig</h1>
          <div className="w-16" />
        </div>

      {/* Summary Card */}
      <Card className="shadow-lg border-0 bg-white/90 backdrop-blur mb-6">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <CardTitle className="text-green-800">Angebot bereit!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Customer Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Building className="h-4 w-4 text-gray-600" />
              <span className="font-medium">Kunde</span>
            </div>
            <p className="text-gray-800">{state.customerName || 'Mobile Kunde'}</p>
            {state.projectName && (
              <p className="text-sm text-gray-600 mt-1">{state.projectName}</p>
            )}
          </div>

          {/* Totals */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Netto:</span>
              <span>{formatCurrency(totals.net)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">MwSt.:</span>
              <span>{formatCurrency(totals.tax)}</span>
            </div>
            <div className="border-t pt-2">
              <div className="flex justify-between font-semibold text-lg">
                <span>Gesamt:</span>
                <span className="text-green-600">{formatCurrency(totals.net + totals.tax)}</span>
              </div>
            </div>
          </div>

          {/* Items Summary */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Positionen</span>
            </div>
            <p className="text-sm text-gray-700">
              {state.selectedItems.size} ausgewählte Position{state.selectedItems.size !== 1 ? 'en' : ''}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Items Preview */}
      <Card className="shadow-lg border-0 bg-white/90 backdrop-blur mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-purple-600" />
            Angebots-Vorschau
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {quoteItems.map((item, index) => (
              <div key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {item.name || item.beschreibung}
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.menge} × {formatCurrency(item.preis)} / {item.einheit}
                  </p>
                </div>
                <div className="text-right ml-2">
                  <p className="font-medium text-green-600">
                    {formatCurrency(item.preis * item.menge)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <Card className="shadow-lg border-0 bg-white/90 backdrop-blur mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-600" />
            Export-Optionen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
      <div className="space-y-3">
            {/* PDF Export - Main export option */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800">PDF Export</span>
              </div>
              <p className="text-sm text-blue-700 mb-3">
                Professionelles PDF-Angebot mit allen Positionen
              </p>
              <Button
                onClick={handleExport}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 mobile-touch-feedback"
                disabled={state.isLoading}
              >
                {state.isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Erstelle...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    PDF exportieren
                  </>
                )}
              </Button>
            </div>

          </div>

          {/* Export Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Export-Info</span>
            </div>
            <div className="text-xs text-gray-600 space-y-1">
              <p>• Dokumente werden automatisch heruntergeladen</p>
              <p>• PDF ist der empfohlene Standard-Export</p>
              <p>• Alle Preise inkl. MwSt. ausgewiesen</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button 
          variant="outline" 
          onClick={() => setState(prev => ({ ...prev, phase: 'input', selectedItems: new Map() }))}
          className="w-full h-12 mobile-touch-feedback"
        >
          Neues Angebot erstellen
        </Button>
        
        <Button 
          variant="ghost" 
          onClick={() => setState(prev => ({ ...prev, phase: 'processing' }))}
          className="w-full h-10 mobile-touch-feedback"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Positionen bearbeiten
        </Button>
      </div>
      </div>
    </div>
  );

  // Render current phase with error boundary
  try {
    return (
      <div className="mobile-app">
        {state.phase === 'input' && renderInputPhase()}
        {state.phase === 'processing' && renderProcessingPhase()}
        {state.phase === 'output' && renderOutputPhase()}
        
        {/* Debug component - only in development */}
        <MobileDebug enabled={process.env.NODE_ENV === 'development'} />
      </div>
    );
  } catch (error) {
    console.error('Mobile app render error:', error);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Anwendungsfehler</h2>
            <p className="text-gray-600 mb-4">
              Es ist ein unerwarteter Fehler aufgetreten. Bitte laden Sie die Seite neu.
            </p>
            <Button 
              onClick={() => window.location.reload()} 
              className="w-full"
            >
              Seite neu laden
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
} 
